import {get, merge, set, omit, unset, remove, has} from 'lodash';

// Rename a key inside a parent object while preserving the original
// insertion order. The naive `unset(old) + set(new)` pair drops the
// old key and re-adds the new one at the END of the parent — visible
// to the user as the renamed property jumping to the bottom of the
// schema table (and, if past the 25-row pagination cutoff, paginating
// itself out of view). Instead, walk the parent's keys and rebuild
// it in place with the renamed key occupying the same slot.
//
// Both paths are dot-paths (e.g. `properties.foo`, `items.properties.bar`,
// or even a single segment for top-level renames). Falls back to the
// old behavior when the parent isn't a plain object — e.g. an array,
// or a missing intermediate node — so callers never see a worse outcome
// than they did before.
export function renameKeyPreserveOrder(targetSchema, fromPath, toPath, value) {
    const fromSegments = String(fromPath).split('.');
    const toSegments = String(toPath).split('.');
    const oldKey = fromSegments[fromSegments.length - 1];
    const newKey = toSegments[toSegments.length - 1];
    const parentSegments = fromSegments.slice(0, -1);
    const parent = parentSegments.length === 0 ? targetSchema : get(targetSchema, parentSegments);

    if (!parent || typeof parent !== 'object' || Array.isArray(parent)) {
        unset(targetSchema, fromPath);
        set(targetSchema, toPath, value);
        return;
    }

    const reordered = {};
    let replaced = false;
    for (const k of Object.keys(parent)) {
        if (k === oldKey) {
            reordered[newKey] = value;
            replaced = true;
        } else if (k === newKey) {
            // Target name already occupies a different slot — drop it;
            // the renamed entry takes the old slot. (When oldKey ===
            // newKey the first branch already handled it; we never
            // reach this branch in that case.)
            continue;
        } else {
            reordered[k] = parent[k];
        }
    }
    if (!replaced) {
        // Old key wasn't found — match the prior fallback and append.
        reordered[newKey] = value;
    }

    for (const k of Object.keys(parent)) delete parent[k];
    Object.assign(parent, reordered);
}

// Pure reducer extracted from SchemaBlock so it can be unit-tested
// without the React component tree. Operates on an already-cloned
// targetSchema and mutates it in place; the component's
// handleChangeSet does the cloneDeep before calling in.
export function applyChangeSet(targetSchema, changeSet) {
    switch (changeSet.op) {
        case 'remove':
            unset(targetSchema, changeSet.propName || changeSet.key);
            if (!targetSchema.type) {
                targetSchema.type = 'object';
                targetSchema.properties = {};
            }
            break;
        case 'add':
            changeSet.fields.map((fieldSpec) => {
                if (fieldSpec.$ref) {
                    if (fieldSpec.type === 'array') {
                        set(targetSchema, fieldSpec.propName, {
                            type: 'array',
                            items: {
                                type: 'object',
                                $ref: fieldSpec.$ref,
                            },
                        });
                    } else {
                        set(targetSchema, fieldSpec.propName, {
                            type: fieldSpec.type || 'object',
                            $ref: fieldSpec.$ref,
                        });
                    }
                } else {
                    const obj = {
                        type: fieldSpec.type || 'string',
                        title: fieldSpec.title || fieldSpec.name,
                        description: fieldSpec.description || '',
                    };
                    if (fieldSpec.format) {
                        obj.format = fieldSpec.format;
                    }
                    if (fieldSpec.type === 'object') {
                        obj.properties = {};
                    } else if (fieldSpec.type === 'combined') {
                        obj[fieldSpec.operator] = [];
                        unset(obj, 'type');
                    } else if (fieldSpec.type === 'array') {
                        obj.items = {
                            type: 'object',
                            properties: {},
                        };
                    }
                    set(targetSchema, fieldSpec.propName, obj);
                }
            });
            break;
        case 'replace':
            targetSchema.properties[changeSet.key] = changeSet.field;
            break;
        case 'add-schema-ref':
            targetSchema[changeSet.operator].push({$ref: changeSet.link});
            break;
        case 'change-combine-operator':
            set(targetSchema, changeSet.operator, get(targetSchema, changeSet.old));
            unset(targetSchema, changeSet.old);
            break;
        case 'rm-schema-ref': {
            if (changeSet.link) {
                remove(targetSchema[changeSet.operator], (remoteSchema) => remoteSchema.$ref === changeSet.link);
            } else {
                targetSchema[changeSet.operator].splice(changeSet.key, 1);
            }
            const remaining = targetSchema[changeSet.operator];
            if (Array.isArray(remaining) && remaining.length === 0) {
                // Last entry removed — drop the combine key entirely
                // so CombinedSchemasView doesn't keep rendering an
                // empty "Must match all of / No data" strip.
                unset(targetSchema, changeSet.operator);
            } else if (Array.isArray(remaining) && remaining.length === 1 && changeSet.operator === 'allOf') {
                // Only one entry left in an allOf — the wrapper is
                // now redundant. Inline schemas merge their contents
                // into the parent; a bare $ref hoists to a top-level
                // $ref. Either way the result is an equivalent,
                // simpler spec.
                const lone = remaining[0];
                unset(targetSchema, 'allOf');
                if (lone && typeof lone.$ref === 'string') {
                    targetSchema.$ref = lone.$ref;
                } else if (lone && typeof lone === 'object') {
                    merge(targetSchema, lone);
                }
            }
            break;
        }
        case 'set-required':
            if (changeSet.required) {
                set(targetSchema, changeSet.propName, get(targetSchema, changeSet.propName) || []);
                get(targetSchema, changeSet.propName).push(changeSet.key);
            } else {
                remove(get(targetSchema, changeSet.propName), (key) => key === changeSet.key);
            }
            set(targetSchema, changeSet.propName, get(targetSchema, changeSet.propName) || []);
            break;
        case 'change-schema-type': {
            let changedSchema = targetSchema;
            if (changeSet.schemaPath) {
                changedSchema = get(targetSchema, changeSet.schemaPath);
                if (!changedSchema) {
                    let mimeTypeArray = changeSet.schemaPath.split('.');
                    mimeTypeArray = mimeTypeArray.slice(2, mimeTypeArray.length - 1);
                    let mimeType = mimeTypeArray.join('.');
                    let pathArray = changeSet.schemaPath.split('.', 2);
                    pathArray.push(mimeType);
                    pathArray.push('schema');
                    changedSchema = get(targetSchema, pathArray);
                }
            }
            if (['allOf', 'oneOf', 'anyOf'].indexOf(changeSet.targetType) !== -1) {
                changedSchema[changeSet.targetType] = [];
                unset(changedSchema, 'items');
                unset(changedSchema, 'type');
                unset(changedSchema, 'properties');
            } else {
                if (get(changedSchema, 'items.properties') || get(changedSchema, 'properties')) {
                    if (changedSchema.type === 'array') {
                        changedSchema.type = changeSet.targetType;
                        changedSchema.properties = get(changedSchema, 'items.properties') || get(changedSchema, 'properties');
                        unset(changedSchema, 'items');
                    } else {
                        changedSchema.type = changeSet.targetType;
                        changedSchema.items = {properties: get(changedSchema, 'properties') || get(changedSchema, 'items.properties')};
                        unset(changedSchema, 'properties');
                    }
                } else {
                    if (has(changedSchema, 'items')) {
                        unset(changedSchema, 'items');
                    } else {
                        unset(changedSchema, 'properties');
                    }
                    changedSchema.type = 'object';
                    changedSchema.properties = {};
                }
            }
            break;
        }
        case 'set-schema-ref': {
            // "Add $ref" with no field name means "my schema combined with
            // the referenced one". Per OpenAPI 3.0 ($ref siblings are
            // undefined) and the portable 3.1 idiom, that is allOf
            // composition — not a wholesale replacement. Previously this
            // handler unset `properties` outright, destroying every local
            // field the user had already defined.
            //
            // Rules:
            // - If the schema already has local content (`properties`,
            //   `items.properties`, or an existing `allOf`), compose via
            //   allOf so CombinedSchemasView renders each entry with its
            //   own delete button.
            // - If the schema is empty, set the $ref directly — same as
            //   before. No regression for the fresh-stub workflow.
            let changedSchema = targetSchema;
            if (changeSet.schemaPath) {
                changedSchema = get(targetSchema, changeSet.schemaPath);
            }

            if (changeSet.type === 'array') {
                const localItemProps = get(changedSchema, 'items.properties');
                const localItemType = get(changedSchema, 'items.type') || 'object';
                unset(changedSchema, 'title');
                unset(changedSchema, 'properties');
                changedSchema.type = 'array';
                if (localItemProps && Object.keys(localItemProps).length > 0) {
                    changedSchema.items = {
                        allOf: [
                            {$ref: changeSet.link},
                            {type: localItemType, properties: localItemProps},
                        ],
                    };
                } else {
                    changedSchema.items = {$ref: changeSet.link};
                }
            } else {
                const hasProps = changedSchema.properties && Object.keys(changedSchema.properties).length > 0;
                const hasAllOf = Array.isArray(changedSchema.allOf);

                if (hasAllOf) {
                    changedSchema.allOf.push({$ref: changeSet.link});
                } else if (hasProps) {
                    const localPortion = {type: 'object', properties: changedSchema.properties};
                    if (Array.isArray(changedSchema.required) && changedSchema.required.length > 0) {
                        localPortion.required = changedSchema.required;
                    }
                    unset(changedSchema, 'properties');
                    unset(changedSchema, 'required');
                    unset(changedSchema, 'type');
                    unset(changedSchema, 'title');
                    unset(changedSchema, 'items');
                    changedSchema.allOf = [{$ref: changeSet.link}, localPortion];
                } else {
                    unset(changedSchema, 'type');
                    unset(changedSchema, 'title');
                    unset(changedSchema, 'items');
                    unset(changedSchema, 'properties');
                    changedSchema.$ref = changeSet.link;
                }
            }
            break;
        }
        case 'add-inline-schema':
            targetSchema[changeSet.operator].push(changeSet.schema);
            break;
        case 'add-example':
            targetSchema.example = changeSet.example;
            break;
        case 'rm-example':
            unset(targetSchema, 'example');
            break;
        case 'update-example':
            targetSchema.example = changeSet.example;
            break;
        case 'toggle-additional-properties': {
            let changedSchema = targetSchema;
            if (changeSet.schemaPath) {
                changedSchema = get(targetSchema, changeSet.schemaPath);
            }
            changedSchema.additionalProperties = changeSet.additionalProperties;
            break;
        }
        default:
            const target = get(targetSchema, changeSet.propName || changeSet.key);
            target && merge(target, omit(changeSet, 'key', 'propName', '$resolvedRef', 'state'));

            if (changeSet.enum) {
                target.enum = changeSet.enum;
            }

            if (changeSet.name) {
                const val = get(targetSchema, changeSet.key);
                renameKeyPreserveOrder(targetSchema, changeSet.key, changeSet.propName, val);

                // Also rename inside the grandparent's `required` array
                // if the old name is listed there. The grandparent owns
                // both `properties` and `required`, so for a path like
                // `properties.iban` (or `items.properties.iban`) we drop
                // the last two segments to find it. Without this the
                // renamed property loses its required flag the moment
                // schema-view rebuilds — the indexOf(name) check fails
                // because `required` still references the old key.
                const fromSegments = String(changeSet.key).split('.');
                const toSegments = String(changeSet.propName).split('.');
                const oldName = fromSegments[fromSegments.length - 1];
                const newName = toSegments[toSegments.length - 1];
                const grandparentPath = fromSegments.slice(0, -2);
                const grandparent = grandparentPath.length === 0
                    ? targetSchema
                    : get(targetSchema, grandparentPath);
                if (grandparent && Array.isArray(grandparent.required)) {
                    const idx = grandparent.required.indexOf(oldName);
                    if (idx !== -1) {
                        grandparent.required[idx] = newName;
                    }
                }
            }

            break;
    }

    return targetSchema;
}
